import { useTranslation } from 'react-i18next';
import { useTrackNavigationClicked } from '../../utils/analytics';
import { isGenAiLogomakerAvailable } from '../../utils/featureAvailability';
import { logoProductsPricing } from '../../utils/pricing';
import { proLogoDesignUrl } from '../../utils/urls';
import type { Locale } from '../../i18n/config';

export type LogoProductKey = 'logomaker' | 'logo-essential' | 'logo-contest';

type LogoProductTile = {
    title: string;
    description: string;
    imageDefault: { src: string; altText: string };
    linkHref: string;
    ctaText: string;
    priceText?: string;
    ctaRef: (node: HTMLElement) => void;
};

type LogoProductTilesConfig = Record<LogoProductKey, LogoProductTile>;

export const useLogoProductTilesConfig = (
    trackingCategory: string,
    locale: Locale,
): LogoProductTilesConfig => {
    const { t } = useTranslation();

    const classicLogoProductTiles = {
        logomaker: {
            title: t('Vista Logos > Logo Product Tile > Logomaker.productTileHeader'),
            description: t('Vista Logos > Logo Product Tile > Logomaker.productTileSubHeader'),
            imageDefault: {
                src: t('Vista Logos > Logo Product Tile > Logomaker.productImage.src'),
                altText: '',
            },
            linkHref: t('Vista Logos > Logo Product Tile > Logomaker.url.contentfulUrl'),
            ctaText: t('Vista Logos > Logo Product Tile > Logomaker.urlText'),
            priceText: t('Vista Logos > Logo Product Tile > Logomaker > Price Text.value'),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                t('Vista Logos > Logo Product Tile > Logomaker.url.contentfulUrl'),
                'createYourFreeLogo',
            ),
        },
        'logo-essential': {
            title: t('Vista Logos > Logo Product Tile > Logo Essential.productTileHeader'),
            description: t('Vista Logos > Logo Product Tile > Logo Essential.productTileSubHeader'),
            imageDefault: {
                src: t('Vista Logos > Logo Product Tile > Logo Essential.productImage.src'),
                altText: '',
            },
            linkHref: proLogoDesignUrl(locale),
            ctaText: t('Vista Logos > Logo Product Tile > Logo Essential.urlText'),
            priceText: t('Vista Logos > Logo Product Tile > Logo Essential > Price Text.value', {
                logoEssentialPriceAmount: logoProductsPricing[locale]['logo-essential'],
            }),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                proLogoDesignUrl(locale),
                'seePackageDetails',
            ),
        },
        'logo-contest': {
            title: t('Vista Logos > Logo Product Tile > Logo Contest.productTileHeader'),
            description: t('Vista Logos > Logo Product Tile > Logo Contest.productTileSubHeader'),
            imageDefault: {
                src: t('Vista Logos > Logo Product Tile > Logo Contest.productImage.src'),
                altText: '',
            },
            linkHref: t('Vista Logos > Logo Product Tile > Logo Contest.url.contentfulUrl'),
            ctaText: t('Vista Logos > Logo Product Tile > Logo Contest.urlText'),
            priceText: t('Vista Logos > Logo Product Tile > Logo Contest > Price Text.value', {
                logoContestBronzePriceAmount: logoProductsPricing[locale]['logo-contest-bronze'],
            }),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                t('Vista Logos > Logo Product Tile > Logo Contest.url.contentfulUrl'),
                'seeLogoContestDetails',
            ),
        },
    };

    const genAiLogoProductTiles = {
        logomaker: {
            title: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.productTileHeader',
            ),
            description: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.productTileSubHeader',
            ),
            imageDefault: {
                src: t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.productImage.src',
                ),
                altText: t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.productImage.altText',
                ),
            },
            linkHref: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.url.contentfulUrl',
            ),
            ctaText: t('Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.urlText'),
            priceText: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logomaker Price Text.value',
            ),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                t('Logomaker GenAi Landing Page > Logo Product Tile > Logomaker.url.contentfulUrl'),
                'createYourFreeLogo',
            ),
        },
        'logo-essential': {
            title: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Essential.productTileHeader',
            ),
            description: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Essential.productTileSubHeader',
            ),
            imageDefault: {
                src: t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logo Essential.productImage.src',
                ),
                altText: t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logo Essential.productImage.altText',
                ),
            },
            linkHref: proLogoDesignUrl(locale),
            ctaText: t('Logomaker GenAi Landing Page > Logo Product Tile > Logo Essential.urlText'),
            priceText: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Essential Price Text.value',
                {
                    logoEssentialPriceAmount: logoProductsPricing[locale]['logo-essential'],
                },
            ),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                proLogoDesignUrl(locale),
                'seePackageDetails',
            ),
        },
        'logo-contest': {
            title: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.productTileHeader',
            ),
            description: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.productTileSubHeader',
            ),
            imageDefault: {
                src: t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.productImage.src',
                ),
                altText: t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.productImage.altText',
                ),
            },
            linkHref: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.url.contentfulUrl',
            ),
            ctaText: t('Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.urlText'),
            priceText: t(
                'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest Price Text.value',
                {
                    logoContestBronzePriceAmount:
                        logoProductsPricing[locale]['logo-contest-bronze'],
                },
            ),
            ctaRef: useTrackNavigationClicked(
                trackingCategory,
                'Logo Product Tiles',
                t(
                    'Logomaker GenAi Landing Page > Logo Product Tile > Logo Contest.url.contentfulUrl',
                ),
                'seeLogoContestDetails',
            ),
        },
    };

    return isGenAiLogomakerAvailable(locale) ? genAiLogoProductTiles : classicLogoProductTiles;
};
